import { useState, useMemo, useEffect, useCallback } from "react";
import { useVelvet } from "@/context/VelvetContext";
import { supabase, apiFetch } from "@/lib/supabase";
import { fmtDate } from "@/lib/calc";
import StatusBadge from "@/components/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Search, Mail, Phone, MapPin, StickyNote } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const { customers, quotations, orders, reloadCustomers } = useVelvet();
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return customers;
    return customers.filter((c) =>
      (c.name || '').toLowerCase().includes(s) ||
      (c.email || '').toLowerCase().includes(s) ||
      (c.phone || '').toLowerCase().includes(s)
    );
  }, [customers, q]);

  const selected = customers.find((c) => c.id === selectedId);

  const add = async (data) => {
    const { error } = await supabase.from('customers').insert(data);
    if (error) return toast.error(error.message);
    toast.success(`${data.name} added`);
    setAddOpen(false);
    reloadCustomers();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-[1400px]" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
        <div>
          <div className="velvet-eyebrow mb-3">Directory</div>
          <h1 className="velvet-page-title">Customers</h1>
          <p className="text-sm text-muted-foreground mt-3">Every arrangement begins with someone we know.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 w-56 rounded-sm" data-testid="customer-search-input" />
          </div>
          <Button onClick={() => setAddOpen(true)} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="add-customer-btn">
            <Plus className="h-4 w-4 mr-1" /> New customer
          </Button>
        </div>
      </div>

      <div className="velvet-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-foreground/15 text-left">
                <th className="px-6 py-3 velvet-eyebrow">Name</th>
                <th className="px-6 py-3 velvet-eyebrow">Contact</th>
                <th className="px-6 py-3 velvet-eyebrow hidden lg:table-cell">Address</th>
                <th className="px-6 py-3 velvet-eyebrow text-right">Quotations</th>
                <th className="px-6 py-3 velvet-eyebrow text-right">Orders</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const qCount = quotations.filter((x) => x.customer_id === c.id).length;
                const oCount = orders.filter((x) => x.customer_id === c.id).length;
                return (
                  <tr key={c.id} className="border-b border-border hover:bg-[hsl(var(--muted))]/60 cursor-pointer" onClick={() => setSelectedId(c.id)} data-testid={`customer-row-${c.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-serif text-lg">{c.name}{c.is_demo && <span className="ml-2 text-[9px] align-middle px-1 py-0.5 border border-border text-muted-foreground uppercase tracking-wider">DEMO</span>}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{c.notes}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{c.email || '—'}</div>
                      <div className="text-xs text-muted-foreground">{c.phone || '—'}</div>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell text-muted-foreground max-w-xs truncate">{c.address || '—'}</td>
                    <td className="px-6 py-4 text-right velvet-money">{qCount}</td>
                    <td className="px-6 py-4 text-right velvet-money">{oCount}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={5} className="px-6 py-16 text-center text-muted-foreground">No customers match.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <AddCustomerDialog open={addOpen} onOpenChange={setAddOpen} onSubmit={add} />
      <CustomerDetailDialog customer={selected} quotations={quotations} orders={orders} onClose={() => setSelectedId(null)} />
    </div>
  );
}

function AddCustomerDialog({ open, onOpenChange, onSubmit }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', notes: '' });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="add-customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl tracking-tight">New customer</DialogTitle>
          <DialogDescription>Add a person or account.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div><Label>Name</Label><Input value={form.name} onChange={set('name')} className="rounded-sm" data-testid="new-customer-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input value={form.email} onChange={set('email')} className="rounded-sm" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={set('phone')} className="rounded-sm" /></div>
          </div>
          <div><Label>Address</Label><Input value={form.address} onChange={set('address')} className="rounded-sm" /></div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={set('notes')} className="rounded-sm" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!form.name.trim()} onClick={() => onSubmit(form)} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="save-customer-btn">
            Save customer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomerDetailDialog({ customer, quotations, orders, onClose }) {
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [busy, setBusy] = useState(false);

  const loadNotes = useCallback(async () => {
    if (!customer) return;
    try {
      const data = await apiFetch(`/crm/notes?customer_id=${customer.id}`);
      setNotes(Array.isArray(data) ? data : []);
    } catch (e) { /* silent — role may not allow */ setNotes([]); }
  }, [customer]);

  useEffect(() => {
    if (customer) { setNoteBody(""); loadNotes(); }
  }, [customer, loadNotes]);

  const addNote = async () => {
    if (!noteBody.trim() || !customer) return;
    setBusy(true);
    try {
      await apiFetch('/crm/notes', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customer.id, body: noteBody.trim() }),
      });
      setNoteBody("");
      toast.success('Nota agregada');
      await loadNotes();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!customer) return null;
  const cQ = quotations.filter((q) => q.customer_id === customer.id);
  const cO = orders.filter((o) => o.customer_id === customer.id);

  return (
    <Dialog open={!!customer} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="customer-detail-dialog">
        <DialogHeader>
          <div className="velvet-eyebrow">Customer</div>
          <DialogTitle className="font-serif text-3xl">{customer.name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <Info icon={Mail} label="Email" value={customer.email || '—'} />
          <Info icon={Phone} label="Phone" value={customer.phone || '—'} />
          <Info icon={MapPin} label="Address" value={customer.address || '—'} full />
          <Info icon={StickyNote} label="Notes" value={customer.notes || '—'} full />
        </div>

        <div className="mt-6" data-testid="customer-crm-notes">
          <div className="velvet-eyebrow mb-3 flex items-center gap-2"><StickyNote className="h-3.5 w-3.5" /> CRM Notes</div>
          <div className="space-y-2 mb-3">
            <Textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
                      placeholder="Añadir una nota…"
                      className="rounded-sm min-h-[70px]" data-testid="customer-new-note-input" />
            <Button size="sm" onClick={addNote} disabled={busy || !noteBody.trim()}
                    className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                    data-testid="customer-add-note-btn">
              Guardar nota
            </Button>
          </div>
          {notes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin notas CRM todavía.</p>
          ) : (
            <div className="space-y-2 max-h-[240px] overflow-y-auto">
              {notes.map((n) => (
                <div key={n.id} className="border border-border p-3 bg-card" data-testid={`customer-note-${n.id}`}>
                  <div className="text-sm whitespace-pre-wrap">{n.body}</div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mt-1">
                    {n.author_name || '—'} · {fmtDate(n.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6">
          <div className="velvet-eyebrow mb-3">Quotations</div>
          {cQ.length === 0 ? <p className="text-sm text-muted-foreground">No quotations yet.</p> : (
            <ul className="divide-y divide-border border border-border">
              {cQ.map((q) => (
                <li key={q.id} className="flex items-center justify-between px-4 py-3">
                  <div><div className="font-medium">{q.number}</div><div className="text-xs text-muted-foreground">{fmtDate(q.created_at)}</div></div>
                  <StatusBadge status={q.status} kind="quotation" />
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="mt-6">
          <div className="velvet-eyebrow mb-3">Orders</div>
          {cO.length === 0 ? <p className="text-sm text-muted-foreground">No orders yet.</p> : (
            <ul className="divide-y divide-border border border-border">
              {cO.map((o) => (
                <li key={o.id} className="flex items-center justify-between px-4 py-3">
                  <div><div className="font-medium">{o.number}</div><div className="text-xs text-muted-foreground">{o.occasion} · {fmtDate(o.delivery_date)}</div></div>
                  <StatusBadge status={o.status} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Info({ icon: Icon, label, value, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <div className="velvet-eyebrow mb-1 flex items-center gap-2"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
